// src/components/studio-upload-meta/studio-upload-meta.mjs

import { LitElement, html } from 'lit';
import styles from './studio-upload-meta.css.mjs';
import '../studio-button/studio-button.mjs';
import '../studio-icon/studio-icon.mjs';
import '../studio-input/studio-input.mjs';
import '../studio-tooltip/studio-tooltip.mjs';

const studioUploadMetaDefaults = {
	name: '',
	'content-type': '',
	'content-length': 0,
	id: '',
	credit: '',
	caption: '',
	tags: [],
	'created-at': '',
	'created-by': '',
	'updated-at': '',
	'updated-by': '',
}

export class StudioUploadMeta extends LitElement {
	static styles = styles;

	static properties = {
		config			: { type: Object },
		upload			: { type: Boolean },
		confirmOnly	: { type: Boolean },
		collection	: { type: String },		// view | edit | create
	};

	constructor() {
		super();
		this.config = studioUploadMetaDefaults;
		this.collection = 'view';
		this.confirmOnly = false;
	};
	
	// Merge provided config with defaults
	set config(value) {
		this._config = { ...studioUploadMetaDefaults, ...value };
    this.requestUpdate();
  }

	get config() {
		return this._config;
	}

	render() {
		return html`
			<div class="studio-upload-meta">
				<slot name="title">
					<div class="title">
						<div class="top-right">${this.closeButton(this.upload)}</div>
						${this.config.name}
					</div>
				</slot>
				<div class="loading-bar-container"><slot name="loading-bar"></slot></div>
				<div class="fixed-metadata">
					<div class="thumbnail-container">
						${this.thumbnailButtons(this.upload)}
						<slot name="thumbnail"></slot>
					</div>
					<div class="metadata-container">
						${this.metadataRows(this.config)}
					</div>
				</div>
				<div class="update-metadata">
					<div class="input-container">
						<div class="metadata-label">Update asset's metadata below</div>
						${this.metadataInputs(this.config)}
					</div>
					<div class="collection-container">
						<div class="title-tooltip">
							<studio-text variant="s2-subtitle">
								Add asset to content packages
							</studio-text>
							<studio-tooltip
								position="right"
								text="Filter Content Packages is case-sensitive and filters by the prefix "Content Package for ".
								You only need to type the specific Content Package name that follows the prefix."
								trigger="click">
									<studio-icon 
										name="circle-info" 
										color="var(--color-gray-4, #7C7C7C)"
									></studio-icon>
							</studio-tooltip>
						</div>
						<slot name="collection"></slot>
					</div>
				</div>
				<div class="action-buttons-container">
					<slot name="action-buttons">
						${this.actionButtons(this.config)}
					</slot>
				</div>
			</div>
		`;
		
	}
	
	closeButton(upload) {
		if (this.confirmOnly) return '';
		if (upload) {
			const deleteEvent = new CustomEvent('delete-asset', {
				detail: this.config.id, bubbles: true, composed: true
			})
			return html`
				<studio-button
					size="small" state="default" style-type="filled" icon="trash"
					@click="${() => this.dispatchEvent(deleteEvent)}">
    			Delete
  			</studio-button>`;
		}

		const closeEvent = new CustomEvent('close-asset', {
			detail: this.config.id, bubbles: true, composed: true
		})

		return html`
			<studio-button
				size="small" state="default" style-type="clear" icon="circle-xmark"
				@click="${() => this.dispatchEvent(closeEvent)}">
			</studio-button>`;
	}
	
	thumbnailButtons(upload) {
		if (!upload) {
			const replaceEvent = new CustomEvent('replace-asset', {
				detail: this.config.id, bubbles: true, composed: true
			});
			const downloadEvent = new CustomEvent('download-asset', {
				detail: this.config.id, bubbles: true, composed: true
			})
			return html`
				<div class="thumbnail-buttons">
					<studio-button class="replace-button" size="small" state="default"
						style-type="filled" icon="rotate" 
						@click="${() => this.dispatchEvent(replaceEvent)}">
						 Replace
  				</studio-button>
					<studio-button class="download-button" size="small" state="default"
						style-type="filled" icon="download"
						@click="${() => this.dispatchEvent(downloadEvent)}">
						 Download
					</studio-button>
				</div>`;
		}
	}

	metadataRows(config) {
		const rows = [
			{ icon: 'address-card', label: 'ID:', value: config['id'] },
			{ icon: 'file', label: 'Content type:', value: config['content-type'] },
			{ icon: 'calendar-days', label: 'Updated at:', 
					value: formatISOToLocalTime(config['updated-at'])},
			{ icon: 'user', label: 'Updated by:', value: config['updated-by'] },
			{ icon: 'database', label: 'Size:', 
					value: bytesToSize(config['content-length']) },
		].filter(row => !(this.upload && row.label === 'ID:'));

		return rows.map(row => html`
			<div class="metadata-row">
				<studio-icon class="icon" name="${row.icon}"
					size="1rem" color="inherit"></studio-icon>
				<div class="label">${row.label}</div>
				<div class="value">${row.value}</div>
			</div>
		`);
	}

	metadataInputs(config) {
		const inputs = [
			{ label: 'File name', value: config.name,
					field: 'name', placeholder: 'Required. File name'},
			{ label: 'Description', value: config.caption,
					field: 'caption', placeholder: 'Required. 200 character limit'},
			{ label: 'Credit (Copyright holder)', value: config.credit,
					field: 'credit', placeholder: 'Optional. Name of copyright holder'},
		];

		return inputs.map(input => html`
			<studio-input class="input"
				size="medium" state="default" style-type="outline"
				label="${input.label}" placeholder="${input.placeholder}"
				.value="${input.value}" id="${input.field}"></studio-input>
		`);
	}

	actionButtons(config) {
		const buttons = [
			{ label: 'Delete', icon: 'trash', x: 0, 
					event: 'delete-asset', detail: () => config.id},
			{ label: 'Share', icon: 'share-from-square', x: 1,
					event: 'share-asset', detail: () => config.id},
			{ label: 'Save', icon: 'floppy-disk', x: 2,
					event: 'update-asset', detail: () => this.gatherMetadata()},
		];

		// Calculate the maximum x value to determine the grid size
		const maxX = Math.max(...buttons.map(button => button.x)) + 1;

		const clickEvent = (event, detail) => {
			this.dispatchEvent(new CustomEvent(event, {
				detail: detail(), bubbles: true, composed: true
			}));
		}

		// Create an array to hold all grid items (buttons and placeholders)
		const gridItems = Array(maxX).fill(null).map((_, index) => {
			const button = buttons.find(b => b.x === index);
			if (button) {
				return html`
					<studio-button class="action-button"
						x="${button.x}"
						size="small" state="default"
						style-type="filled" icon="${button.icon}"
						@click="${() => clickEvent(button.event, button.detail)}">
						${button.label}
					</studio-button>
				`;
			}
			return html`<div class="grid-placeholder"></div>`;
		});

		return html`
			<div class="action-buttons-grid" style="--max-x: ${maxX}">
				${gridItems}
			</div>
		`;
	}

	gatherMetadata() {
		const inputs = this.shadowRoot.querySelectorAll('studio-input');
		const metadata = {...this.config,
			'updated-at': new Date().toISOString(),
			'updated-by': 'Self',
		};
		inputs.forEach(input => {
			metadata[input.id] = input.value;
		});
		return metadata;
	}
}


function bytesToSize(bytes) {
	const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
	if (bytes == 0) return 'n/a';
	const i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)));
	return Math.round(bytes / Math.pow(1024, i), 2) + ' ' + sizes[i];
}

function formatISOToLocalTime(isoString) {
    // Validate ISO string
    if (!isoString || typeof isoString !== 'string') {
        return 'Invalid ISO string';
    }

    const date = new Date(isoString);

    // Check if date is valid
    if (isNaN(date.getTime())) {
        return 'Invalid date';
    }

    // Format options for toLocaleString
    const options = {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZoneName: 'short'
    };

    // Convert to local time and format
    return date.toLocaleString('en-US', options).replace(/,/, ' at');
}

customElements.define('studio-upload-meta', StudioUploadMeta);
